import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = ["aria-label", "title", "aria-expanded"]
const _hoisted_2 = /*#__PURE__*/_createStaticVNode("<svg class=\"h-7 w-7\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\" aria-hidden=\"true\"><circle cx=\"9\" cy=\"4\" r=\"2\"></circle><path d=\"M9 7v6h5l3 6h3\"></path><path d=\"M9 10h5\"></path><path d=\"M13 17.5a5 5 0 1 1-5.5-7.4\"></path></svg>", 1)
const _hoisted_3 = [
  _hoisted_2
]
const _hoisted_4 = ["aria-label"]
const _hoisted_5 = { class: "text-lg font-bold text-curaduriaDark" }
const _hoisted_6 = { class: "mb-3 mt-1 text-sm text-gray-600" }
const _hoisted_7 = { class: "space-y-2" }
const _hoisted_8 = ["aria-pressed"]
const _hoisted_9 = /*#__PURE__*/_createElementVNode("span", {
  class: "w-8 text-center text-2xl font-bold",
  "aria-hidden": "true"
}, "T", -1)
const _hoisted_10 = ["aria-pressed"]
const _hoisted_11 = /*#__PURE__*/_createElementVNode("span", {
  class: "h-6 w-6 rounded-full border-2 border-current bg-gradient-to-r from-black from-50% to-white to-50%",
  "aria-hidden": "true"
}, null, -1)
const _hoisted_12 = ["aria-pressed"]
const _hoisted_13 = /*#__PURE__*/_createElementVNode("span", {
  class: "flex w-8 justify-center gap-0.5",
  "aria-hidden": "true"
}, [
  /*#__PURE__*/_createElementVNode("span", { class: "h-3 w-3 rounded-full bg-black" }),
  /*#__PURE__*/_createElementVNode("span", { class: "h-3 w-3 rounded-full bg-gray-400" }),
  /*#__PURE__*/_createElementVNode("span", { class: "h-3 w-3 rounded-full bg-gray-200" })
], -1)
const _hoisted_14 = ["aria-pressed"]
const _hoisted_15 = /*#__PURE__*/_createElementVNode("span", {
  class: "h-6 w-6 rounded border-2 border-current bg-white",
  "aria-hidden": "true"
}, null, -1)

import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";

type AccessibilityPreference =
  | "largeText"
  | "highContrast"
  | "grayscale"
  | "whiteBackground";

interface AccessibilityPreferences {
  largeText: boolean;
  highContrast: boolean;
  grayscale: boolean;
  whiteBackground: boolean;
}

type StoredAccessibilityPreferences = Omit<
  AccessibilityPreferences,
  "whiteBackground"
> &
  Partial<Pick<AccessibilityPreferences, "whiteBackground">>;


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const storageKey = "accessibilityPreferences";
const classNames = [
  "a11y-large-text",
  "a11y-high-contrast",
  "a11y-grayscale",
  "a11y-white-background",
];

const preferences = reactive<AccessibilityPreferences>({
  largeText: false,
  highContrast: false,
  grayscale: false,
  whiteBackground: false,
});

const isOpen = ref(false);
const menuRoot = ref<HTMLElement | null>(null);
const menuButton = ref<HTMLButtonElement | null>(null);
const firstOption = ref<HTMLButtonElement | null>(null);

const openMenu = async () => {
  isOpen.value = true;
  await nextTick();
  firstOption.value?.focus();
};

const closeMenu = (returnFocus = false) => {
  isOpen.value = false;
  if (returnFocus) {
    nextTick(() => menuButton.value?.focus());
  }
};

const toggleMenu = () => {
  if (isOpen.value) {
    closeMenu();
  } else {
    openMenu();
  }
};

const handlePointerDown = (event: PointerEvent) => {
  if (
    isOpen.value &&
    event.target instanceof Node &&
    !menuRoot.value?.contains(event.target)
  ) {
    closeMenu();
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (isOpen.value && event.key === "Escape") {
    event.preventDefault();
    closeMenu(true);
  }
};

const isStoredAccessibilityPreferences = (
  value: unknown
): value is StoredAccessibilityPreferences => {
  if (!value || typeof value !== "object") return false;

  const candidate = value as Partial<AccessibilityPreferences>;
  return (
    typeof candidate.largeText === "boolean" &&
    typeof candidate.highContrast === "boolean" &&
    typeof candidate.grayscale === "boolean" &&
    (candidate.whiteBackground === undefined ||
      typeof candidate.whiteBackground === "boolean")
  );
};

const applyPreferences = () => {
  const root = document.documentElement;
  root.classList.toggle("a11y-large-text", preferences.largeText);
  root.classList.toggle("a11y-high-contrast", preferences.highContrast);
  root.classList.toggle("a11y-grayscale", preferences.grayscale);
  root.classList.toggle(
    "a11y-white-background",
    preferences.whiteBackground
  );
};

const savePreferences = () => {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ ...preferences }));
  } catch {
    // The controls still work when browser storage is unavailable.
  }
};

const togglePreference = (preference: AccessibilityPreference) => {
  preferences[preference] = !preferences[preference];
};

watch(
  preferences,
  () => {
    applyPreferences();
    savePreferences();
  },
  { deep: true }
);

onMounted(() => {
  try {
    const savedPreferences = localStorage.getItem(storageKey);
    const parsedPreferences = savedPreferences
      ? JSON.parse(savedPreferences)
      : null;

    if (isStoredAccessibilityPreferences(parsedPreferences)) {
      Object.assign(preferences, parsedPreferences);
    }
  } catch {
    // Invalid or unavailable storage falls back to the default settings.
  }

  applyPreferences();
  document.addEventListener("pointerdown", handlePointerDown);
  document.addEventListener("keydown", handleKeyDown);
});

onBeforeUnmount(() => {
  document.documentElement.classList.remove(...classNames);
  document.removeEventListener("pointerdown", handlePointerDown);
  document.removeEventListener("keydown", handleKeyDown);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "menuRoot",
    ref: menuRoot,
    "data-accessibility-controls": "",
    class: "fixed left-3 top-1/2 z-[100001] -translate-y-1/2 sm:left-5"
  }, [
    _createElementVNode("button", {
      ref_key: "menuButton",
      ref: menuButton,
      type: "button",
      class: "flex h-12 w-12 items-center justify-center rounded-full bg-curaduriaDark text-white shadow-lg transition hover:bg-curaduria focus:outline-none focus:ring-4 focus:ring-curaduria focus:ring-offset-2",
      "aria-label": _ctx.$t(isOpen.value ? 'accessibility.closeMenu' : 'accessibility.openMenu'),
      title: _ctx.$t(isOpen.value ? 'accessibility.closeMenu' : 'accessibility.openMenu'),
      "aria-expanded": isOpen.value,
      "aria-controls": "accessibility-options",
      onClick: toggleMenu
    }, _hoisted_3, 8, _hoisted_1),
    _createVNode(_Transition, {
      "enter-active-class": "transition duration-150 ease-out",
      "enter-from-class": "-translate-y-2 opacity-0",
      "enter-to-class": "translate-y-0 opacity-100",
      "leave-active-class": "transition duration-100 ease-in",
      "leave-from-class": "translate-y-0 opacity-100",
      "leave-to-class": "-translate-y-2 opacity-0"
    }, {
      default: _withCtx(() => [
        (isOpen.value)
          ? (_openBlock(), _createElementBlock("section", {
              key: 0,
              id: "accessibility-options",
              class: "absolute left-0 top-full mt-3 max-h-[calc(50vh-4rem)] w-64 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-4 text-gray-900 shadow-2xl",
              "aria-label": _ctx.$t('accessibility.title')
            }, [
              _createElementVNode("h2", _hoisted_5, _toDisplayString(_ctx.$t("accessibility.title")), 1),
              _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t("accessibility.description")), 1),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("button", {
                  ref_key: "firstOption",
                  ref: firstOption,
                  type: "button",
                  class: _normalizeClass(["accessibility-option", { 'accessibility-option-active': _unref(preferences).largeText }]),
                  "aria-pressed": _unref(preferences).largeText,
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (togglePreference('largeText')))
                }, [
                  _hoisted_9,
                  _createElementVNode("span", null, _toDisplayString(_ctx.$t("accessibility.largeText")), 1)
                ], 10, _hoisted_8),
                _createElementVNode("button", {
                  type: "button",
                  class: _normalizeClass(["accessibility-option", { 'accessibility-option-active': _unref(preferences).highContrast }]),
                  "aria-pressed": _unref(preferences).highContrast,
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (togglePreference('highContrast')))
                }, [
                  _hoisted_11,
                  _createElementVNode("span", null, _toDisplayString(_ctx.$t("accessibility.highContrast")), 1)
                ], 10, _hoisted_10),
                _createElementVNode("button", {
                  type: "button",
                  class: _normalizeClass(["accessibility-option", { 'accessibility-option-active': _unref(preferences).grayscale }]),
                  "aria-pressed": _unref(preferences).grayscale,
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (togglePreference('grayscale')))
                }, [
                  _hoisted_13,
                  _createElementVNode("span", null, _toDisplayString(_ctx.$t("accessibility.grayscale")), 1)
                ], 10, _hoisted_12),
                _createElementVNode("button", {
                  type: "button",
                  class: _normalizeClass(["accessibility-option", { 'accessibility-option-active': _unref(preferences).whiteBackground }]),
                  "aria-pressed": _unref(preferences).whiteBackground,
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (togglePreference('whiteBackground')))
                }, [
                  _hoisted_15,
                  _createElementVNode("span", null, _toDisplayString(_ctx.$t("accessibility.whiteBackground")), 1)
                ], 10, _hoisted_14)
              ])
            ], 8, _hoisted_4))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 512))
}
}

})